'use client'
import { SessionProvider } from 'next-auth/react'
import { useEffect } from 'react'
import { I18nextProvider } from 'react-i18next'
import { DEFAULT_PAGE_TITLE } from '@/context/constants'
import ThemeAwareToastContainer from '@/components/ThemeAwareToastContainer'
import LocaleSync from '@/components/LocaleSync'
import DocumentTitleSync from '@/components/DocumentTitleSync'
import MobileFormZoomGuard from '@/components/MobileFormZoomGuard'
import ScrollMenuTouchFix from '@/components/ScrollMenuTouchFix'
import CapacitorSafeArea from '@/components/CapacitorSafeArea'
import i18n from '@/i18n'
import { LayoutProvider } from '@/context/useLayoutContext'
import { NotificationProvider } from '../../context/useNotificationContext'
import { ChildrenType } from '../../types/component-props'

const hideNativeSplash = () => {
  const cap = (window as Window & { Capacitor?: { Plugins?: { SplashScreen?: { hide?: () => Promise<void> } } } }).Capacitor
  void cap?.Plugins?.SplashScreen?.hide?.()
}

const AppProvidersWrapper = ({ children }: ChildrenType) => {
  const handleChangeTitle = () => {
    if (document.visibilityState == 'hidden') document.title = 'Please come back 🥺'
    else document.title = DEFAULT_PAGE_TITLE
  }

  useEffect(() => {
    const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    const isNative = Boolean(cap?.isNativePlatform?.())
    if (!isNative) return

    const minVisibleMs = 1200
    const started = performance.now()
    let hideTimer: number | null = null

    const scheduleHide = () => {
      if (hideTimer !== null) return
      const elapsed = performance.now() - started
      const delay = Math.max(0, minVisibleMs - elapsed)
      hideTimer = window.setTimeout(hideNativeSplash, delay)
    }

    scheduleHide()
    document.addEventListener('visibilitychange', handleChangeTitle)

    return () => {
      if (hideTimer !== null) window.clearTimeout(hideTimer)
      document.removeEventListener('visibilitychange', handleChangeTitle)
    }
  }, [])

  return (
    <SessionProvider>
      <I18nextProvider i18n={i18n}>
        <LocaleSync />
        <DocumentTitleSync />
        <MobileFormZoomGuard />
        <ScrollMenuTouchFix />
        <CapacitorSafeArea />
        <LayoutProvider>
          <NotificationProvider>
            {children}
            <ThemeAwareToastContainer />
          </NotificationProvider>
        </LayoutProvider>
      </I18nextProvider>
    </SessionProvider>
  )
}
export default AppProvidersWrapper
